import { Component } from '@theme/component';
import {
  CartAddEvent,
  CartUpdateEvent,
  DiscountUpdateEvent,
  QuantitySelectorUpdateEvent,
  ThemeEvents,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { debounce, fetchConfig, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { CustomCollectionFlowComponent } from '@theme/custom-collection-flow';
import { StepInfoManager } from '@theme/custom-collection-utility';
import { initCartProfileCleanup } from '@theme/cart-profile-cleanup';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);

    // Cart profile cleanup: remove items for profiles that no longer exist (runs on normal cart & subscription modal)
    initCartProfileCleanup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * @returns {Set<string>}
   */
  #getCartSectionsToUpdate() {
    const sectionsToUpdate = new Set([this.sectionId]);
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) sectionsToUpdate.add(item.dataset.sectionId);
    });
  
    return sectionsToUpdate;
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const remove = () => cartItemRowToRemove.remove();

    if (prefersReducedMotion()) return remove();

    // Add class to the row to trigger the animation
    cartItemRowToRemove.style.setProperty('--row-height', `${cartItemRowToRemove.clientHeight}px`);
    cartItemRowToRemove.classList.add('removing');

    // Remove the row after the animation ends
    onAnimationEnd(cartItemRowToRemove, remove);
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    try {
      const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

      this.#disableCartItems();

      const { line, quantity } = config;
      const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);

    const subscriptionModalSectionId = document.querySelector('[data-tv-subscription-cart-modal][data-section-id]')?.getAttribute('data-section-id');
    if (subscriptionModalSectionId) {
      sectionsToUpdate.add(subscriptionModalSectionId);
    }

    // Add specific sections for re-rendering (more targeted approach)
    const mainContent = document.getElementById('MainContent');
    if (mainContent) {
      const collectionStickyFooter = mainContent.querySelector('custom-collection-footer-component');
      if (collectionStickyFooter) {
        const collectionStickyFooterSectionId = collectionStickyFooter.getAttribute('data-section-id');
        if (collectionStickyFooterSectionId) {
          sectionsToUpdate.add(collectionStickyFooterSectionId);
        }
      }

      // Do NOT add collection-subscription-footer: same as add-to-cart flow.
      // Morphing it on quantity change causes next-button flicker (enabled → disabled).
      // The footer syncs via JS on CartUpdateEvent (#syncAddButtonsForStepQuota).
    }

    const mealDeliveryMomentAppSection = document.querySelector('.meal-delivery-moment-app-section');
    if (mealDeliveryMomentAppSection) {
      const mealDeliveryMomentAppSectionId = mealDeliveryMomentAppSection.getAttribute('data-section-id');
      if (mealDeliveryMomentAppSectionId) {
        sectionsToUpdate.add(mealDeliveryMomentAppSectionId);
      }
    }

    const deliveryMomentSection = document.querySelector('delivery-moment-component');
    if (deliveryMomentSection) {
      const deliveryMomentSectionId = deliveryMomentSection.closest('[id^="shopify-section-"]')?.id.replace('shopify-section-', '');
      if (deliveryMomentSectionId) {
        sectionsToUpdate.add(deliveryMomentSectionId);
      }
    }

    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    CartLoadManager.addCartLoader();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Cart update failed: ${response.status} ${response.statusText}`);
        }
        return response.text();
      })
      .then((responseText) => {
        let parsedResponseText;
        try {
          parsedResponseText = JSON.parse(responseText);
        } catch (parseError) {
          throw new Error('Failed to parse cart response');
        }

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Derive productId from line for targeted product card overlay update (remove/qty change)
        let productId = null;
        if (parsedResponseText.items_removed?.length > 0) {
          productId = parsedResponseText.items_removed[0]?.product_id;
        } else if (parsedResponseText.items?.length > 0 && line) {
          const idx = Math.max(0, Number(line) - 1);
          productId = parsedResponseText.items[idx]?.product_id;
        }

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
            productId: productId ?? undefined,
          })
        );

        sectionsToUpdate.forEach(sectionId => {
          if (parsedResponseText.sections[sectionId]) {
            try {
              morphSection(sectionId, parsedResponseText.sections[sectionId]);
            } catch (error) {
              console.warn(`Failed to morph section ${sectionId}:`, error);
            }
          }
        });

        const remainingCartTotal = parsedResponseText.total_price;

        // If cart total is less than the minimum order value, we need to go back to the first step of the flow
        const customCollectionFlowComponent = /** @type {CustomCollectionFlowComponent | null} */ (document.querySelector('custom-collection-flow-component'));
        const stepInfo = StepInfoManager.getStepInfo();
        /** @type {number} */
        const stepCount = stepInfo ? /** @type {any} */ (stepInfo)['step-count'] : 1;
        if (customCollectionFlowComponent) {
          const minimumOrderValue = parseFloat(customCollectionFlowComponent.getAttribute('data-minimum-order-value') || '0');
          if(((remainingCartTotal)/100) < minimumOrderValue && stepCount > 1) {
            customCollectionFlowComponent.updateFlowStepWithProductFiltering();
          }
        }

        // Find existing delivery drawer instance and call initializeSelection
        const deliveryDrawer = document.querySelector('delivery-times-drawer-component');
        if (deliveryDrawer && typeof /** @type {any} */ (deliveryDrawer).initializeSelection === 'function') {
          /** @type {any} */ (deliveryDrawer).initializeSelection();
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        CartLoadManager.removeCartLoader();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
    } catch (error) {
      console.error(error);
      this.#enableCartItems();
    }
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    try {
      const quantitySelector = this.refs.quantitySelectors[line - 1];
      const quantityInput = quantitySelector?.querySelector('input');

      if (!quantityInput) {
        const error = new Error('Quantity input not found');
        throw error;
      }

      quantityInput.value = quantityInput.defaultValue;

      const cartItemError = this.refs[`cartItemError-${line}`];
      const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

      if (!(cartItemError instanceof HTMLElement)) {
        const error = new Error('Cart item error not found');
        throw error;
      }

      if (!(cartItemErrorContainer instanceof HTMLElement)) {
        const error = new Error('Cart item error container not found');
        throw error;
      }

      cartItemError.textContent = parsedResponseText.errors;
      cartItemErrorContainer.classList.remove('hidden');
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    try {
      const { sectionId } = this.dataset;

      if (!sectionId) {
        const error = new Error('Section id missing');
        throw error;
      }

      return sectionId;
    } catch (error) {
      console.error(error);
      throw error;
    }
  }
}

class CartLoadManager extends Component {
  static addCartLoader() {
    try {
      const element = document.querySelector('cart-items-component .cart-drawer__content .cart-items__wrapper');
      if(element) {
        const loadContainer = document.createElement('div');
        loadContainer.className = 'load-container';
        loadContainer.id = 'load-container';
        
        const loadSpinner = document.createElement('div');
        loadSpinner.className = 'load';
        
        loadContainer.appendChild(loadSpinner);
        element.appendChild(loadContainer);
      }
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  static removeCartLoader() {
    try {
      const loadContainer = document.querySelector('cart-items-component .cart-drawer__content .cart-items__wrapper .load-container');
      if(loadContainer) {
        loadContainer.remove();
      }
    } catch (error) {
      console.error(error);
      throw error;
    }
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
